import { useEffect, useState, useRef } from "react";
import { api, formatErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, PriorityBadge } from "@/components/app/Badges";
import { toast } from "sonner";
import { Clock, MapPin, Phone, User, Wrench, Camera, X, Loader2, Image as ImageIcon, Hash } from "lucide-react";

export default function ComplaintDetailDialog({ complaintId, open, onOpenChange, onChanged }) {
  const { user, config } = useAuth();
  const [c, setC] = useState(null);
  const [note, setNote] = useState("");
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState([]); // [{id, preview}]
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // photo id
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [releaseReason, setReleaseReason] = useState("");
  const [releaseDetail, setReleaseDetail] = useState("");
  const fileInputRef = useRef(null);

  async function load() {
    if (!complaintId) return;
    try {
      const { data } = await api.get(`/complaints/${complaintId}`);
      setC(data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  }

  useEffect(() => {
    if (!open) return;
    load();
    if (user?.role === "admin") {
      api.get("/users", { params: { role: "technician" } }).then((r) => setTechnicians(r.data)).catch(() => {});
    }
    return () => {
      pendingPhotos.forEach((p) => URL.revokeObjectURL(p.preview));
      setPendingPhotos([]);
    };
  }, [open, complaintId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function patch(update) {
    setLoading(true);
    try {
      await api.patch(`/complaints/${complaintId}`, update);
      toast.success("Updated");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setLoading(false); }
  }

  async function submitNote(e) {
    e.preventDefault();
    if (!note.trim() && pendingPhotos.length === 0) return;
    setLoading(true);
    try {
      if (note.trim()) {
        await api.post(`/complaints/${complaintId}/notes`, { note });
      }
      if (pendingPhotos.length > 0) {
        await api.post(`/complaints/${complaintId}/photos`, { photo_ids: pendingPhotos.map((p) => p.id) });
      }
      setNote("");
      pendingPhotos.forEach((p) => URL.revokeObjectURL(p.preview));
      setPendingPhotos([]);
      await load();
      onChanged && onChanged();
      toast.success("Update posted");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }

  async function handlePhotoChange(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (pendingPhotos.length >= 6) { toast.error("Max 6 photos per update"); break; }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const preview = URL.createObjectURL(file);
        setPendingPhotos((p) => [...p, { id: data.id, preview }]);
      }
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  }

  function removePending(id) {
    setPendingPhotos((p) => {
      const rm = p.find((x) => x.id === id);
      if (rm) URL.revokeObjectURL(rm.preview);
      return p.filter((x) => x.id !== id);
    });
  }

  if (!c) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-3xl">
          <DialogHeader><DialogTitle>Loading…</DialogTitle></DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  const canTechAct = user?.role === "technician" && c.assigned_to === user.id && !["Resolved", "Closed"].includes(c.status);
  const canCustomerClose = user?.role === "customer" && c.status === "Resolved";
  const canAddPhoto = config.uploads_enabled && (
    (user?.role === "customer" && c.customer_id === user.id) ||
    (user?.role === "technician" && c.assigned_to === user.id) ||
    user?.role === "admin"
  );
  const token = typeof window !== "undefined" ? localStorage.getItem("np_token") : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="complaint-detail-dialog" className="bg-slate-950 border-slate-800 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-mono-alt text-purple-300 text-sm tracking-widest">{c.ticket_no}</DialogTitle>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <StatusBadge status={c.status} />
            <PriorityBadge priority={c.priority} />
            <span className="text-xs text-slate-400">{c.complaint_type}</span>
          </div>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-4 pt-2">
          <div className="tactical-card p-4 space-y-2 text-sm">
            <h4 className="font-semibold text-slate-200 mb-2">Customer</h4>
            <div className="flex items-center gap-2 text-slate-300"><User className="w-4 h-4 text-slate-500" />{c.customer_name || "—"}</div>
            {c.customer_account_id && (
              <div className="flex items-center gap-2 text-slate-300"><Hash className="w-4 h-4 text-slate-500" /><span className="font-mono-alt text-purple-300">{c.customer_account_id}</span></div>
            )}
            <div className="flex items-center gap-2 text-slate-300"><Phone className="w-4 h-4 text-slate-500" />{c.customer_phone || "—"}</div>
            <div className="flex items-center gap-2 text-slate-300"><MapPin className="w-4 h-4 text-slate-500" />{c.location} · <span className="text-slate-400">{c.address}</span></div>
          </div>
          <div className="tactical-card p-4 space-y-2 text-sm">
            <h4 className="font-semibold text-slate-200 mb-2">Ownership</h4>
            <div className="flex items-center gap-2 text-slate-300"><Wrench className="w-4 h-4 text-slate-500" />Technician: {c.assigned_to_name || "Unassigned"}</div>
            <div className="flex items-center gap-2 text-slate-400"><Clock className="w-4 h-4 text-slate-500" />Created: {new Date(c.created_at).toLocaleString()}</div>
            {c.resolved_at && <div className="flex items-center gap-2 text-emerald-400"><Clock className="w-4 h-4" />Resolved: {new Date(c.resolved_at).toLocaleString()}</div>}
          </div>
        </div>

        <div className="tactical-card p-4 mt-2">
          <h4 className="font-semibold text-slate-200 mb-1">Description</h4>
          <p className="text-sm text-slate-300 whitespace-pre-wrap">{c.description}</p>
        </div>

        {(c.photos?.length > 0) && (
          <div className="tactical-card p-4">
            <h4 className="font-semibold text-slate-200 mb-3 flex items-center gap-2"><ImageIcon className="w-4 h-4 text-purple-300" /> Photos <span className="text-xs text-slate-500">({c.photos.length})</span></h4>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {c.photos.map((p) => (
                <button key={p.id} type="button" onClick={() => setLightbox(p.id)} className="relative aspect-square rounded-md overflow-hidden border border-slate-700 bg-slate-900 hover:border-purple-500/60" data-testid={`photo-thumb-${p.id}`}>
                  <img src={`${API}/files/${p.id}?auth=${token}`} alt={p.filename} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </div>
        )}

        {user?.role === "admin" && (
          <div className="tactical-card p-4 grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Assign Technician</Label>
              <Select value={c.assigned_to || ""} onValueChange={(v) => patch({ assigned_to: v })}>
                <SelectTrigger data-testid="assign-technician-select" className="bg-slate-900 border-slate-700 mt-1"><SelectValue placeholder="Select technician" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  {technicians.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name} · {t.location || "—"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Priority</Label>
              <Select value={c.priority} onValueChange={(v) => patch({ priority: v })}>
                <SelectTrigger data-testid="priority-select" className="bg-slate-900 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  {["Low", "Medium", "High", "Critical"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Status</Label>
              <Select value={c.status} onValueChange={(v) => patch({ status: v })}>
                <SelectTrigger data-testid="admin-status-select" className="bg-slate-900 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  {["Open", "Assigned", "In Progress", "Resolved", "Closed"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {user?.role === "technician" && !c.assigned_to && (
          <div className="tactical-card p-4 flex flex-wrap gap-2 items-center bg-purple-500/5 border-purple-500/40">
            <span className="text-sm text-slate-300 flex-1">This ticket is unassigned. Claim it to start work.</span>
            <Button data-testid="tech-self-assign-btn" disabled={loading} onClick={() => patch({ assigned_to: user.id })} className="bg-purple-500 hover:bg-purple-600">
              <Wrench className="w-4 h-4 mr-1.5" /> Assign to me
            </Button>
          </div>
        )}

        {canTechAct && (
          <div className="tactical-card p-4 flex flex-wrap gap-2">
            <Button data-testid="tech-mark-in-progress" disabled={loading || c.status === "In Progress"} onClick={() => patch({ status: "In Progress" })} className="bg-indigo-500 hover:bg-indigo-600">Mark In Progress</Button>
            <Button data-testid="tech-mark-resolved" disabled={loading} onClick={() => patch({ status: "Resolved" })} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950">Mark Resolved</Button>
            {user?.role === "technician" && c.assigned_to === user.id && !["Resolved", "Closed"].includes(c.status) && (
              <Button
                data-testid="tech-release-btn"
                disabled={loading}
                onClick={() => { setReleaseReason(""); setReleaseDetail(""); setReleaseOpen(true); }}
                variant="outline"
                className="border-rose-500/40 text-rose-200 hover:bg-rose-500/10"
              >
                Release ticket
              </Button>
            )}
          </div>
        )}
        {canCustomerClose && (
          <div className="tactical-card p-4">
            <Button data-testid="customer-close-btn" disabled={loading} onClick={() => patch({ status: "Closed" })} className="bg-slate-700 hover:bg-slate-600">Close Complaint</Button>
          </div>
        )}

        <div className="tactical-card p-4">
          <h4 className="font-semibold text-slate-200 mb-3">Timeline</h4>
          <ol className="relative border-l border-slate-800 ml-2 space-y-4">
            {(c.timeline || []).slice().reverse().map((t, i) => (
              <li key={i} className="ml-4">
                <div className="absolute w-2 h-2 rounded-full bg-purple-500 -left-1 mt-2" />
                <div className="text-xs font-mono-alt text-slate-500">{new Date(t.ts).toLocaleString()}</div>
                <div className="text-sm text-slate-200"><span className="text-purple-300">{t.actor}</span> <span className="text-slate-500">({t.actor_role})</span> — {t.message}</div>
              </li>
            ))}
          </ol>
        </div>

        <form onSubmit={submitNote} className="tactical-card p-4 space-y-3">
          <Label className="text-slate-300">Add update</Label>
          <Textarea data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Provide an update…" className="bg-slate-900 border-slate-700 min-h-[80px]" />

          {canAddPhoto && (
            <div>
              <div className="flex flex-wrap gap-2">
                {pendingPhotos.map((p) => (
                  <div key={p.id} className="relative w-20 h-20 rounded-md overflow-hidden border border-slate-700 bg-slate-900">
                    <img src={p.preview} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removePending(p.id)} className="absolute top-0.5 right-0.5 bg-rose-500/90 hover:bg-rose-600 text-white rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {pendingPhotos.length < 6 && (
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="detail-add-photo" className="w-20 h-20 flex flex-col items-center justify-center gap-1 border border-dashed border-slate-700 rounded-md text-slate-400 hover:border-purple-500/60 hover:text-purple-300">
                    {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                    <span className="text-[9px] font-mono-alt uppercase tracking-wider">{uploading ? "Uploading" : "Photo"}</span>
                  </button>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={handlePhotoChange} />
            </div>
          )}

          <Button data-testid="submit-note-btn" type="submit" disabled={loading || uploading || (!note.trim() && pendingPhotos.length === 0)} className="bg-purple-500 hover:bg-purple-600">Post update</Button>
        </form>

        {lightbox && (
          <div onClick={() => setLightbox(null)} className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out" data-testid="photo-lightbox">
            <img src={`${API}/files/${lightbox}?auth=${token}`} alt="" className="max-w-full max-h-full object-contain rounded" />
            <button className="absolute top-4 right-4 text-white bg-slate-800/80 rounded-full p-2 hover:bg-slate-700"><X className="w-5 h-5" /></button>
          </div>
        )}
      </DialogContent>

      <Dialog open={releaseOpen} onOpenChange={setReleaseOpen}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-md">
          <DialogHeader><DialogTitle>Release ticket · {c?.ticket_no}</DialogTitle></DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!releaseReason) { toast.error("Pick a reason"); return; }
              if (releaseReason === "Other" && !releaseDetail.trim()) { toast.error("Please describe the reason"); return; }
              setLoading(true);
              try {
                await api.post(`/complaints/${complaintId}/release`, { reason: releaseReason, detail: releaseDetail || null });
                toast.success(`Released — reason logged: ${releaseReason}`);
                setReleaseOpen(false);
                onChanged?.();
                onOpenChange(false);
              } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
              finally { setLoading(false); }
            }}
            className="space-y-3"
          >
            <div className="text-xs text-slate-400">
              This ticket goes back to <b className="text-amber-300">Available</b>. Log a quick reason so the office knows why.
            </div>
            <div className="grid grid-cols-2 gap-2">
              {(config?.release_reasons || ["Not my expertise", "Wrong area", "Customer unreachable", "Other"]).map((r) => (
                <button
                  key={r}
                  type="button"
                  data-testid={`release-reason-${r}`}
                  onClick={() => setReleaseReason(r)}
                  className={`px-3 py-2 rounded-lg border text-sm text-left transition ${releaseReason === r ? "border-rose-500 bg-rose-500/15 text-rose-100" : "border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-600"}`}
                >
                  {r}
                </button>
              ))}
            </div>
            {releaseReason === "Other" && (
              <div>
                <Label>Describe the reason</Label>
                <Textarea
                  data-testid="release-detail"
                  rows={2}
                  value={releaseDetail}
                  onChange={(e) => setReleaseDetail(e.target.value)}
                  placeholder="e.g. Customer requested reschedule for next week"
                  className="bg-slate-900 border-slate-700 mt-1"
                />
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <Button data-testid="confirm-release-btn" type="submit" disabled={loading || !releaseReason} className="flex-1 bg-rose-600 hover:bg-rose-500 text-white">
                {loading ? "Releasing…" : "Release ticket"}
              </Button>
              <Button type="button" onClick={() => setReleaseOpen(false)} variant="outline" className="border-slate-700 hover:bg-slate-800">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
